import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal } from "./Reveal";
import { scrollTo } from "./Nav";
import api from "../lib/api";
import { AERIAL_OPTIONS, DEFAULT_PRICING, TOUR3D_BANDS } from "../lib/pricingDefaults";

const mapPlans = (arr) => arr.map((p) => ({ ...p, price: String(p.price), testid: `plan-${p.key}` }));
const mapPacks = (arr) => arr.map((p) => ({ qty: p.qty, price: String(p.price), testid: `pack-${p.qty}` }));
const mapExtras = (arr) => arr.map((x) => ({ ...x, price: String(x.price), testid: `extra-${x.key}` }));

export const Pricing = () => {
  const { t } = useTranslation();
  const [PLANS, setPlans] = useState(() => mapPlans(DEFAULT_PRICING.packages));
  const [PHOTO_PACKS, setPacks] = useState(() => mapPacks(DEFAULT_PRICING.photo_packs));
  const [EXTRAS, setExtras] = useState(() => mapExtras(DEFAULT_PRICING.extras));

  useEffect(() => {
    api.get("/pricing")
      .then((r) => {
        const d = r.data || {};
        if (d.packages?.length) setPlans(mapPlans(d.packages));
        if (d.photo_packs?.length) setPacks(mapPacks(d.photo_packs));
        if (d.extras?.length) setExtras(mapExtras(d.extras));
      })
      .catch(() => {});
  }, []);

  const pkgTagline = (p) => t(`db.pkg.${p.key}.tagline`, { defaultValue: p.tagline });
  const pkgFeatures = (p) => t(`db.pkg.${p.key}.features`, { returnObjects: true, defaultValue: p.features });
  const extraName = (x) => t(`db.extra.${x.key}.name`, { defaultValue: x.name });
  const extraDesc = (x) => t(`db.extra.${x.key}.desc`, { defaultValue: x.desc });

  // Card pacchetto condivisa: desktop (con testid) e carosello mobile (senza testid duplicati)
  const planCard = (plan, withTestids = true) => (
    <div
      className={`relative flex h-full flex-col border p-8 transition-[border-color,transform] duration-500 hover:-translate-y-2 lg:p-10 ${
        plan.featured
          ? "border-[#D6705A] bg-[#16191D]"
          : "border-white/10 bg-transparent hover:border-white/25"
      }`}
      data-testid={withTestids ? plan.testid : undefined}
    >
      {plan.featured && (
        <span className="absolute -top-3 left-8 bg-[#D6705A] px-3 py-1 font-code text-[9px] uppercase tracking-[0.25em] text-[#0D0F12]" data-testid={withTestids ? "plan-featured-badge" : undefined}>
          {t("pricing.featured")}
        </span>
      )}
      <p className="font-code text-[10px] uppercase tracking-[0.3em] text-[#666]">{pkgTagline(plan)}</p>
      <h3 className="mt-3 font-display text-3xl font-light text-[#F9F8F6]">{plan.name}</h3>
      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-code text-[10px] uppercase tracking-[0.2em] text-[#666]">{t("pricing.from")}</span>
        <span className={`font-display text-6xl font-light leading-none ${plan.featured ? "text-[#D6705A]" : "text-[#F9F8F6]"}`} data-testid={withTestids ? `${plan.testid}-price` : undefined}>
          €{plan.price}
        </span>
      </div>
      <ul className="mt-8 flex-1 space-y-3.5 border-t border-white/10 pt-8">
        {pkgFeatures(plan).map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-[#C9C7C3]">
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${plan.featured ? "text-[#D6705A]" : "text-[#666]"}`} strokeWidth={2} />
            {f}
          </li>
        ))}
      </ul>
      <button
        onClick={() => scrollTo("#prenota")}
        className={`mt-10 w-full rounded-full py-4 font-code text-[10px] uppercase tracking-[0.25em] transition-[background-color,color,border-color,transform] duration-300 ${
          plan.featured
            ? "bg-[#D6705A] text-[#0D0F12] hover:bg-[#F9F8F6]"
            : "border border-white/20 text-[#F9F8F6] hover:border-[#D6705A] hover:text-[#D6705A]"
        }`}
        data-testid={withTestids ? `${plan.testid}-cta` : undefined}
      >
        {t("pricing.bookPlan", { name: plan.name })}
      </button>
    </div>
  );

  return (
  <section id="tariffe" className="relative border-t border-white/5 bg-[#0D0F12] px-6 py-28 lg:px-12 lg:py-40" data-testid="pricing-section">
    <div className="mx-auto max-w-7xl">
      <Reveal className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-code text-[11px] uppercase tracking-[0.35em] text-[#D6705A]">{t("pricing.overline")}</p>
          <h2 className="mt-4 font-display text-4xl font-light leading-tight text-[#F9F8F6] sm:text-5xl lg:text-6xl">
            {t("pricing.h2pre")} <em className="italic text-[#D6705A]">{t("pricing.h2em")}</em>.
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-[#A0A0A0]">
          {t("pricing.intro")}
        </p>
      </Reveal>

      {/* Card pacchetti: griglia su desktop, in colonna su mobile */}
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {PLANS.map((plan, i) => (
          <Reveal key={plan.name} delay={i * 0.12} className="h-full">
            {planCard(plan)}
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-20 border-t border-white/10 pt-16" data-testid="extras-section">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <h3 className="font-display text-3xl font-light text-[#F9F8F6] md:text-4xl">
              {t("pricing.extrasPre")} <em className="italic text-[#D6705A]">{t("pricing.extrasEm")}</em>
            </h3>
            <p className="max-w-sm text-sm leading-relaxed text-[#A0A0A0]">
              {t("pricing.extrasSub")}
            </p>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-12">
            <div className="border border-white/10 p-8 lg:col-span-4" data-testid="photo-packs-card">
              <p className="font-code text-[10px] uppercase tracking-[0.3em] text-[#666]">{t("pricing.packsTitle")}</p>
              <div className="mt-6 space-y-4">
                {PHOTO_PACKS.map((p) => (
                  <div key={p.testid} className="flex items-center justify-between border-b border-white/5 pb-4 last:border-b-0 last:pb-0" data-testid={p.testid}>
                    <span className="font-display text-2xl font-light text-[#F9F8F6]">+{p.qty} {t("booking.photosUnit")}</span>
                    <span className="font-code text-sm text-[#D6705A]">€{p.price}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs leading-relaxed text-[#666]">
                {t("pricing.packsNote")}
              </p>
            </div>

            <div className="lg:col-span-8">
              {EXTRAS.map((x) => (
                <div
                  key={x.testid}
                  className="group flex items-center justify-between gap-6 border-t border-white/10 py-6 transition-colors duration-300 last:border-b hover:border-[#D6705A]/40"
                  data-testid={x.testid}
                >
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-6">
                    <h4 className="font-display text-xl font-light text-[#F9F8F6] transition-colors duration-300 group-hover:text-[#D6705A] md:text-2xl sm:min-w-[220px]">
                      {extraName(x)}
                    </h4>
                    <p className="text-sm text-[#A0A0A0]">
                      {extraDesc(x)}
                      {x.key === "aerei" && (
                        <span className="mt-1.5 block max-w-md text-[11px] leading-relaxed text-[#777]" data-testid="aerial-options-pricing">
                          {AERIAL_OPTIONS.map((o) => `${t(`booking.aerial.${o.key}`)} €${Number(x.price) + o.delta}`).join(" · ")}
                        </span>
                      )}
                      {x.key === "virtual-tour" && (
                        <span className="mt-1.5 block max-w-md text-[11px] leading-relaxed text-[#777]" data-testid="tour-bands-pricing">
                          {TOUR3D_BANDS.map((b) => `${t(`booking.surface.${b.key}`)} ${b.delta == null ? t("booking.onQuote") : `€${Number(x.price) + b.delta}`}`).join(" · ")}
                        </span>
                      )}
                      {x.key === "stagionale" && (
                        <span className="mt-1.5 block max-w-md text-[11px] leading-relaxed text-[#777]" data-testid="seasonal-note-pricing">
                          {t("pricing.seasonalNote")}
                        </span>
                      )}
                    </p>
                  </div>
                  <span className="shrink-0 font-code text-sm text-[#D6705A]">
                    {x.key === "aerei" || x.key === "virtual-tour" ? `${t("pricing.from")} €${x.price}` : `€${x.price}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-6 font-code text-[10px] uppercase tracking-[0.25em] text-[#666]" data-testid="pricing-note">
            {t("pricing.vatNote")}
          </p>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
